import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

export default function useOfflineSubmissionSync() {
  const [hasQueuedRequests, setHasQueuedRequests] = useState(false);
  const [queuedCount, setQueuedCount] = useState(0);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    let isListenerRegistered = false;

    const postInitialQueueLengthRequest = () => {
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'GET_QUEUED_LENGTH' });
      } else {
        console.warn('[React] No active service worker controller');
      }
    };

    const handleMessage = (event) => {
      const { triggeredBy, type, count, success, error } = event.data || {};
      // console.log('[React] SW message received:', event.data);

      if (type === 'QUEUE_LENGTH') {
        setHasQueuedRequests(count > 0);
        setQueuedCount(count);
      }

      if (type === 'REPLAY_RESULT') {
        const label = triggeredBy === 'auto' ? 'automatically' : 'manually';
        if (success) {
          setHasQueuedRequests(false);
          setQueuedCount(0);
          toast.success(`Queued submissions replayed successfully (${label})!`);
        } else {
          toast.error(`Failed to replay submissions: ${error || 'Unknown error'}`);
        }
        postInitialQueueLengthRequest(); // refresh after replay
      }
    };

    const registerListeners = () => {
      if (isListenerRegistered) return;
      isListenerRegistered = true;
      navigator.serviceWorker.addEventListener('message', handleMessage);
      navigator.serviceWorker.addEventListener('controllerchange', postInitialQueueLengthRequest);
      postInitialQueueLengthRequest();
    };

    // Wait for service worker to be ready and controller to exist
    navigator.serviceWorker.ready.then(() => {
      if (!navigator.serviceWorker.controller) {
        console.warn('[React] Service worker ready, but no controller yet');
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          console.log('[React] Controller activated');
          registerListeners();
        });
      } else {
        registerListeners();
      }
    });

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
      navigator.serviceWorker.removeEventListener('controllerchange', postInitialQueueLengthRequest);
    };
  }, []);

  const manuallyTriggerReplay = () => {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'REPLAY_QUEUED_SUBMISSIONS' });
    } else {
      toast.info('Offline functionality not ready. Please wait or refresh.');
    }
  };

  return {
    manuallyTriggerReplay,
    hasQueuedRequests,
    queuedCount,
  };
}

